import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import useSeo from '../hooks/useSeo';
import FaqSection from '../components/FaqSection';
import { CITIES } from '../data/cityCleaning';

/**
 * Hub page for the local "Airbnb / short let cleaning in [city]" pages. Targets
 * "airbnb cleaning uk" / "short let changeover cleaning uk" and gives Google a
 * clean parent that links to every city page (and vice versa).
 */

const FAQ = [
  { question: "How much does Airbnb changeover cleaning cost in the UK?", answer: "Short let changeover cleaning is usually priced per turnaround rather than by the hour, and the cost depends on the size of the property, the city, how tight the turnaround is and whether linen and laundry are included. Ariv is building a local network so hosts can compare fair, transparent rates rather than scrambling for whoever is free on the day." },
  { question: "What is the difference between a changeover clean and a standard clean?", answer: "A changeover clean resets a property between guests on a tight turnaround, covering beds and fresh linen, bathrooms, kitchen, restocking essentials and a quick check for anything damaged or running low. A standard domestic clean is usually slower and does not include the guest ready resets or stock alerts that short stay hosting needs." },
  { question: "Do you have cleaners in my city yet?", answer: "We are honest about this: Ariv is building its local cleaner and trade network city by city, so coverage is still growing and we will not pretend to have people somewhere we do not. As the network launches we will ID and background check the cleaners and tradespeople, so hosts know who is coming. List your place at /become-host and we prioritise the areas with the most host demand, which is the fastest way to bring Ariv to your city." },
  { question: "How much commission does Ariv charge hosts compared with other Airbnb managers?", answer: "Ariv charges a 13 percent host commission, host only so the guest pays no separate fee, which sits below Airbnb's 15 to 18 percent, while traditional UK Airbnb management companies typically take 15 to 30 percent. Founding hosts, the first 100, pay 0 percent for their first 90 days, then 10 percent for the rest of year one, and you still get local cleaning, changeovers and trades when something breaks, with ID and background checks coming as the network launches." },
  { question: "How do I become an Airbnb cleaner with Ariv?", answer: "If you are a cleaner or tradesperson, you can join early at /provider/signup and get matched to local short let work as the network goes live in your area. Getting in now means you help shape coverage in your city before it fills up, and you see the host jobs as they come online." },
];

const SERVICE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Airbnb and short let cleaning and changeovers",
  serviceType: "Short let changeover cleaning",
  provider: { "@type": "Organization", name: "Ariv", legalName: "Brick & Capital Ltd", url: "https://ariv.co.uk/" },
  areaServed: { "@type": "Country", name: "United Kingdom" },
  url: "https://ariv.co.uk/airbnb-cleaning/",
};
const BREADCRUMB_JSONLD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://ariv.co.uk/" },
    { "@type": "ListItem", position: 2, name: "Airbnb cleaning", item: "https://ariv.co.uk/airbnb-cleaning/" },
  ],
};
const JSONLD = [SERVICE_JSONLD, BREADCRUMB_JSONLD];

const LocalCleaningHubPage = () => {
  useSeo({
    title: 'Airbnb & short let cleaning across the UK | Ariv for hosts',
    description: 'Ariv is building a local cleaning and changeover network for UK short stay hosts, city by city. Find your city, list your place, or join as a local cleaner.',
    canonical: 'https://ariv.co.uk/airbnb-cleaning/',
    image: 'https://ariv.co.uk/icons/icon-512.png',
    imageAlt: 'Ariv',
    faq: FAQ,
    jsonLd: JSONLD,
  });

  return (
    <div className="min-h-screen bg-white">
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></Link>
          <Link to="/become-host" className="bg-[#1F3A68] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1F3A68]/90">List your place</Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 pt-4 text-sm text-gray-500">
        <Link to="/" className="hover:text-[#1F3A68]">Home</Link>
        <span className="mx-1">/</span>
        <span className="text-gray-700">Airbnb cleaning</span>
      </div>

      <header className="bg-gradient-to-br from-[#F4F2E9] to-white">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-[#1F3A68] mb-4 leading-tight">
            Airbnb and short let cleaning across the UK
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            Ariv is building a local cleaning and changeover network for short stay hosts, city by city. Pick your city to see where we are starting, list your place to get matched, or join as a local cleaner or tradesperson.
          </p>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 py-10">
        <div className="grid sm:grid-cols-2 gap-4">
          {CITIES.map((c) => (
            <Link
              key={c.slug}
              to={`/airbnb-cleaning/${c.slug}/`}
              className="border border-gray-200 rounded-xl p-5 hover:border-[#1F3A68] hover:bg-[#1F3A68]/5 transition group"
            >
              <p className="inline-flex items-center gap-1 text-xs font-semibold text-[#1F3A68] mb-1">
                <MapPin size={13} /> {c.region}
              </p>
              <p className="text-xl font-bold text-gray-900 flex items-center justify-between">
                {c.name}
                <ArrowRight size={18} className="text-[#1F3A68] opacity-0 group-hover:opacity-100 transition" />
              </p>
              <p className="text-sm text-gray-600 mt-1">Short let cleaning and changeovers in {c.name}.</p>
            </Link>
          ))}
        </div>

        <p className="text-center text-gray-600 mt-10">
          Do not see your city yet? We are adding more.{' '}
          <Link to="/become-host" className="text-[#1F3A68] underline">List your place</Link> and we will prioritise the areas with the most demand.
        </p>
      </section>

      <FaqSection faq={FAQ} />

      <footer className="bg-[#1F3A68] text-white py-8 mt-8">
        <div className="max-w-3xl mx-auto px-6 text-center text-white/70 text-sm">
          <p>&copy; {new Date().getFullYear()} Ariv. A product of Brick &amp; Capital Ltd.</p>
          <p className="text-xs mt-2 text-white/50">Registered in England and Wales, company number 16655788. Registered office: Bowness House, 815 Old Kent Road, London SE15 1NX.</p>
          <div className="flex flex-wrap gap-4 justify-center mt-3">
            <Link to="/become-host" className="hover:text-white">List your place</Link>
            <Link to="/airbnb-host-fee-alternative/" className="hover:text-white">Airbnb fee alternative</Link>
            <Link to="/trust" className="hover:text-white">Trust &amp; Safety</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LocalCleaningHubPage;
